import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { beesClient, BeeResponse } from '../../api/beesClient.js';

interface BranchGuardData {
  violations: Array<{
    branch: string;
    rule: string;
    severity: 'high' | 'medium' | 'low';
    message: string;
    timestamp: string;
  }>;
  protectedBranches: string[];
  totalChecks: number;
  passedChecks: number;
  lastScan: string;
  status: 'clean' | 'violations' | 'error';
}

const BranchGuardPanel: React.FC = () => {
  const [guardData, setGuardData] = useState<BranchGuardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBranchGuardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response: BeeResponse = await beesClient.runBranchGuard();
      
      if (response.success && response.data) {
        setGuardData(response.data as BranchGuardData);
      } else {
        const mockData: BranchGuardData = {
          violations: [
            {
              branch: 'feature/unsafe-changes',
              rule: 'Direct push to protected branch',
              severity: 'high',
              message: 'Attempted direct push to main branch without PR',
              timestamp: new Date().toISOString()
            },
            {
              branch: 'hotfix/quick-fix',
              rule: 'Missing required reviewers',
              severity: 'medium', 
              message: 'PR created without required code review approval',
              timestamp: new Date(Date.now() - 3600000).toISOString()
            }
          ],
          protectedBranches: ['main', 'production', 'staging'],
          totalChecks: 15,
          passedChecks: 13,
          lastScan: new Date().toISOString(),
          status: 'violations'
        };
        setGuardData(mockData);
      }
    } catch (err) {
      setError('Failed to fetch branch guard data');
      console.error('Branch guard data fetch error:', err);
      
      const mockData: BranchGuardData = {
        violations: [],
        protectedBranches: ['main', 'production'],
        totalChecks: 10,
        passedChecks: 10,
        lastScan: new Date().toISOString(),
        status: 'clean'
      };
      setGuardData(mockData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranchGuardData();
    const interval = setInterval(fetchBranchGuardData, 60000);
    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity: 'high' | 'medium' | 'low') => {
    switch (severity) {
      case 'high': return 'text-red-400 bg-red-900/20 border-red-500/30';
      case 'medium': return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
      case 'low': return 'text-blue-400 bg-blue-900/20 border-blue-500/30';
      default: return 'text-gray-400 bg-gray-900/20 border-gray-500/30';
    }
  };

  const getStatusIcon = () => {
    if (!guardData) return <Shield className="w-5 h-5 text-gray-400" />;
    
    switch (guardData.status) {
      case 'clean':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'violations':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-400" />;
      default:
        return <Shield className="w-5 h-5 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Branch Guard Status
          </h2>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-slate-700 rounded mb-2"></div>
                <div className="h-3 bg-slate-700 rounded mb-3 w-1/2"></div>
                <div className="h-2 bg-slate-700 rounded"></div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Branch Guard Status
          </h2>
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
            <div className="text-red-400 text-sm">⚠️ {error}</div>
            <button 
              onClick={fetchBranchGuardData} 
              className="text-xs text-red-300 hover:text-red-200 mt-2 underline flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Retry
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!guardData) {
    return (
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Branch Guard Status
          </h2>
          <div className="bg-gray-900/20 border border-gray-500/30 rounded-xl p-4">
            <div className="text-gray-400 text-sm">No branch guard data available</div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-lg font-semibold text-brand-text mb-6 flex items-center">
          <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
          Branch Guard Status
        </h2>

        <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {getStatusIcon()}
              <div>
                <h3 className="cream-gradient-text text-drop-shadow font-medium">
                  {guardData.status === 'clean' ? 'All Clear' : 
                   guardData.status === 'violations' ? 'Violations Detected' : 'Error'}
                </h3>
                <p className="text-sm text-slate-400">
                  Last scan: {new Date(guardData.lastScan).toLocaleString()}
                </p>
              </div>
            </div>
            <button
              onClick={fetchBranchGuardData}
              className="p-2 bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/30 rounded-lg text-indigo-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-400">{guardData.passedChecks}</div>
              <div className="text-sm text-slate-400">Passed Checks</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-2xl font-bold text-slate-300">{guardData.totalChecks}</div>
              <div className="text-sm text-slate-400">Total Checks</div>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium cream-gradient-text text-drop-shadow mb-2">Protected Branches</h4>
            <div className="flex flex-wrap gap-2">
              {guardData.protectedBranches.map((branch) => (
                <span
                  key={branch}
                  className="px-2 py-1 bg-emerald-900/20 border border-emerald-500/30 rounded text-emerald-400 text-xs"
                >
                  {branch}
                </span>
              ))}
            </div>
          </div>
        </div>

        {guardData.violations.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-md font-medium cream-gradient-text text-drop-shadow flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              Violations ({guardData.violations.length})
            </h3>
            {guardData.violations.map((violation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`border rounded-xl p-4 ${getSeverityColor(violation.severity)}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-slate-800/50 px-2 py-1 rounded">
                      {violation.branch}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      violation.severity === 'high' ? 'bg-red-900/30 text-red-300' :
                      violation.severity === 'medium' ? 'bg-yellow-900/30 text-yellow-300' :
                      'bg-blue-900/30 text-blue-300'
                    }`}>
                      {violation.severity.toUpperCase()}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(violation.timestamp).toLocaleString()}
                  </span>
                </div>
                <div className="mb-2">
                  <div className="font-medium text-sm">{violation.rule}</div>
                  <div className="text-sm text-slate-300 mt-1">{violation.message}</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {guardData.violations.length === 0 && guardData.status === 'clean' && (
          <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">No violations detected</span>
            </div>
            <p className="text-sm text-green-300 mt-1">
              All branch protection rules are being followed correctly.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default BranchGuardPanel;
